// src/pages/plataforma/InscripcionCliente.js
// Formulario público: /i/:clienteId/:idEvento/:vendedorId
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import GlassSpinner from "../../components/GlassSpinner";

const API = "https://mocaub1h8d.execute-api.us-east-1.amazonaws.com/prod";
const validateDocumento = (v) => /^[A-Za-z0-9][A-Za-z0-9 ._-]{2,19}$/.test(String(v || "").trim());

export default function InscripcionCliente() {
  const { clienteId, idEvento, vendedorId } = useParams();

  const [cliente, setCliente]   = useState(null);
  const [evento, setEvento]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]       = useState("");

  const [tandaSeleccionada, setTandaSeleccionada] = useState(null);

  const [form, setForm] = useState({
    first_name: "", last_name: "", dni: "", email: "", whatsapp: "", gender: "", birth_date: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, eRes] = await Promise.all([
          axios.post(`${API}/gestionarClientes`,       { accion: "get", clienteId }),
          axios.post(`${API}/gestionarEventosCliente`, { accion: "get", idEvento }),
        ]);
        setCliente(cRes.data);
        setEvento(eRes.data);
        const tandas = (eRes.data?.tandas || []).filter(t => t.tipo !== "CORTESIA");
        if (tandas.length === 1) setTandaSeleccionada(tandas[0]);
      } catch {}
      setLoading(false);
    };
    load();
  }, [clienteId, idEvento]);

  const colorTicket = cliente?.color_ticket || "#dfa66b";
  const colorFuente = cliente?.color_fuente || cliente?.colorFuente || "#ffffff";
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const validate = () => {
    const { first_name, last_name, dni, email, whatsapp, gender, birth_date } = form;
    if (!first_name || !last_name || !dni || !email || !whatsapp || !gender || !birth_date)
      return "Completá todos los campos obligatorios.";
    if (!validateDocumento(dni)) return "DNI/documento inválido.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Email inválido.";
    if (!/^\+?[\d\s\-()]{7,20}$/.test(whatsapp)) return "WhatsApp inválido (incluí código de país).";
    if (!tandaSeleccionada) return "Seleccioná un tipo de entrada.";
    return null;
  };

  const handleSubmit = async () => {
    setError("");
    const err = validate();
    if (err) return setError(err);
    setSending(true);
    try {
      await axios.post(`${API}/registerInscripcion`, {
        ...form, idEvento, clienteId, vendedorId: vendedorId || "",
        tandaId: tandaSeleccionada.id, tandaLabel: tandaSeleccionada.label,
        tipoPago: tandaSeleccionada.tipo, precio: tandaSeleccionada.precio || 0,
        status: "PENDIENTE",
      });
      setSubmitted(true);
    } catch (e) {
      const msg = e.response?.data?.message || "";
      setError(msg.includes("DNI") || msg.includes("duplicado")
        ? "Ese DNI o email ya está registrado para este evento."
        : "Ocurrió un error. Intentá de nuevo.");
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div style={{ ...styles.page(colorTicket), justifyContent: "center" }}>
      <GlassSpinner size={70} background={colorTicket} />
    </div>
  );

  if (submitted) return (
    <div style={styles.page(colorTicket)}>
      <div style={styles.card}>
        <div style={{ fontSize: 56, textAlign: "center", marginBottom: 12 }}>✅</div>
        <h2 style={{ textAlign: "center", marginBottom: 10 }}>¡Inscripción recibida!</h2>
        <p style={{ textAlign: "center", color: "#555", lineHeight: 1.6 }}>
          Tu solicitud está <strong>pendiente de aprobación</strong>.<br />
          Te llegará tu entrada por email y WhatsApp una vez confirmada.
        </p>
      </div>
    </div>
  );

  const tandas = (evento?.tandas || []).filter(t => t.tipo !== "CORTESIA");


  return (
    <div style={styles.page(colorTicket)}>
      {sending && (
        <div style={styles.loaderOverlay}>
          <GlassSpinner size={80} background={colorTicket} />
        </div>
      )}
      <div style={styles.card}>
        <h2 style={{ textAlign: "center", marginBottom: 4 }}>Inscripción</h2>
        {evento && <p style={{ textAlign: "center", color: "#888", fontSize: 13, marginBottom: 16 }}>{evento.nombre} · {evento.fecha}</p>}

        {/* Tandas */}
        {tandas.length > 0 && (
          <>
            <label style={styles.label}>Tipo de entrada *</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 4 }}>
              {tandas.map(t => (
                <div key={t.id}
                  onClick={() => setTandaSeleccionada(t)}
                  style={{
                    ...styles.tandaOption,
                    border: `2px solid ${tandaSeleccionada?.id === t.id ? colorTicket : "#ddd"}`,
                    background: tandaSeleccionada?.id === t.id ? `${colorTicket}18` : "#fafafa",
                  }}>
                  <span style={{ fontWeight: "700" }}>{t.label}</span>
                  <span style={{ color: "#555", fontSize: 13 }}>
                    {t.tipo === "CORTESIA" ? "Cortesía" : `$${Number(t.precio).toLocaleString("es-AR")}`}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        <label style={styles.label}>Nombre *</label>
        <input style={styles.input} value={form.first_name} onChange={set("first_name")} placeholder="Juan" />
        <label style={styles.label}>Apellido *</label>
        <input style={styles.input} value={form.last_name} onChange={set("last_name")} placeholder="Pérez" />
        <label style={styles.label}>DNI *</label>
        <input style={styles.input} value={form.dni} onChange={set("dni")} placeholder="DNI / Pasaporte" />
        <label style={styles.label}>Email *</label>
        <input style={styles.input} type="email" value={form.email} onChange={set("email")} placeholder="juan@email.com" />
        <label style={styles.label}>WhatsApp * (con código de país)</label>
        <input style={styles.input} type="tel" value={form.whatsapp} onChange={set("whatsapp")} placeholder="+5491112345678" />
        <label style={styles.label}>Sexo *</label>
        <select style={styles.input} value={form.gender} onChange={set("gender")}>
          <option value="">Seleccioná...</option>
          <option value="M">Masculino</option>
          <option value="F">Femenino</option>
          <option value="Otro">Otro</option>
        </select>
        <label style={styles.label}>Fecha de nacimiento *</label>
        <input style={styles.input} type="date" value={form.birth_date} onChange={set("birth_date")} />

        {tandaSeleccionada?.tipo === "PAGO" && (
          <p style={{ fontSize: 12, color: "#888", marginTop: 12, textAlign: "center" }}>
            💳 El comprobante de pago se solicita después de que el vendedor apruebe tu solicitud.
          </p>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <button style={{ ...styles.btn, background: colorTicket, color: colorFuente }} onClick={handleSubmit}>
          Enviar inscripción
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: bg => ({ background: bg, minHeight: "100vh", padding: "30px 16px", display: "flex", justifyContent: "center", alignItems: "flex-start" }),
  loaderOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 },
  card:    { background: "#fff", width: "100%", maxWidth: 420, padding: 24, borderRadius: 18, boxShadow: "0 4px 16px rgba(0,0,0,0.2)" },
  label:   { display: "block", fontWeight: "600", fontSize: 13, marginTop: 12, marginBottom: 4, color: "#444" },
  input:   { padding: "11px 14px", borderRadius: 10, width: "100%", border: "1px solid #ccc", fontSize: 15, boxSizing: "border-box", background: "#fafafa" },
  fileBtn: { display: "block", padding: "11px 14px", borderRadius: 10, border: "1.5px dashed #ccc", fontSize: 14, color: "#555", cursor: "pointer", textAlign: "center", marginTop: 4, background: "#fafafa" },
  btn:     { color: "#000", padding: 14, width: "100%", borderRadius: 10, border: "none", marginTop: 20, cursor: "pointer", fontWeight: "bold", fontSize: 16 },
  error:   { color: "red", marginTop: 10, textAlign: "center", fontSize: 13 },
  tandaOption: { padding: "12px 14px", borderRadius: 10, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" },
};
